<template>
  <div class="transmission-item">
    <template v-if="isStandardScreen">
      <div class="file-name">
        <img class="trans-img" :src="getIconBySuffix(task.fileName)" />
        <EllipsisComponent v-if="isUpload || !isDesktopEnv" :file-name="task.fileName" />
        <a-tooltip v-else placement="top" :mouse-enter-delay="TOOLTIP_SHOW_IN_SECONDS">
          <template #title>
            <span>{{ toolTipMsg }}</span>
          </template>
          <EllipsisComponent :file-name="task.fileName" />
        </a-tooltip>
      </div>
      <div v-if="task.transmissionTaskType !== TransmissionTaskType.ZIP" class="progress-container">
        <div class="progress-bar">
          <a-progress
            :percent="progress"
            size="small"
            :show-info="false"
            :stroke-color="progressColumnColor.strokeColor"
          />
        </div>
        <div v-if="state === TaskState.GOING" class="progress-text">{{ progressPresent }}</div>
        <div v-else class="label-status" :style="{ color: progressColumnColor.labelColor }">
          <!-- <span>
          {{ labelSpeed }}
        </span> -->
          <span>
            {{ labelState }}
          </span>
        </div>
      </div>
      <div v-else class="progress-container">
        <div v-if="state === TaskState.GOING" class="progress-text" style="padding-left: 0">
          {{ zipProgress }}
        </div>
        <div v-else class="label-status" :style="{ color: progressColumnColor.labelColor }">
          <span style="margin-right: 8px">
            {{ labelState }}
          </span>
          <a-tooltip
            :mouse-enter-delay="TOOLTIP_SHOW_IN_SECONDS"
            overlay-class-name="tooltipOverlayClassName"
            :title="task.failedReasonTips"
            color="white"
          >
            <img
              v-if="state === TaskState.FAILED"
              src="@assets/svgs/preload-icon-transmission-item-warning.svg"
              alt=""
            />
          </a-tooltip>
        </div>
      </div>
      <div class="operation">
        <div class="operation-container">
          <div class="operation-btn">
            <img
              v-if="state === TaskState.SUCCESS && isUpload"
              src="@assets/svgs/preload-icon-transmission-item-open.svg"
              alt=""
              @click="onOpenInParentDir"
            />
            <img
              v-else-if="state === TaskState.FAILED && task.transmissionTaskType !== TransmissionTaskType.ZIP"
              src="@assets/svgs/preload-icon-transmission-item-refresh.svg"
              alt=""
              @click="onResume"
            />
            <img
              v-else-if="!(!isUpload && state === TaskState.SUCCESS)"
              src="@assets/svgs/preload-icon-transmission-item-cancel.svg"
              alt=""
              @click="onDelete"
            />
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <img class="trans-img" :src="getIconBySuffix(task.fileName)" />
      <div class="transmission-container">
        <div class="transmission-info-container">
          <EllipsisComponent :file-name="task.fileName" />
          <div v-if="task.transmissionTaskType !== TransmissionTaskType.ZIP" class="progress-container">
            <div class="progress-bar">
              <a-progress
                :percent="progress"
                size="small"
                :show-info="false"
                :stroke-color="progressColumnColor.strokeColor"
                :class="{ isSuccess: state === TaskState.SUCCESS }"
              />
            </div>
            <div v-if="state === TaskState.GOING" class="progress-text">{{ progressPresent }}</div>
            <div v-else class="label-status" :style="{ color: progressColumnColor.labelColor }">
              <span>
                {{ labelState }}
              </span>
            </div>
          </div>
          <div v-else class="progress-container">
            <div v-if="state === TaskState.GOING" class="progress-text" style="padding-left: 0">
              {{ zipProgress }}
            </div>
            <div v-else class="label-status" :style="{ color: progressColumnColor.labelColor, paddingLeft: '0px' }">
              <span style="margin-right: 8px">
                {{ labelState }}
              </span>
              <a-tooltip
                :mouse-enter-delay="TOOLTIP_SHOW_IN_SECONDS"
                overlay-class-name="tooltipOverlayClassName"
                :title="task.failedReasonTips"
                color="white"
              >
                <img
                  v-if="state === TaskState.FAILED"
                  src="@assets/svgs/preload-icon-transmission-item-warning.svg"
                  alt=""
                />
              </a-tooltip>
            </div>
          </div>
        </div>
        <div class="operation">
          <div class="operation-container">
            <div class="operation-btn">
              <img
                v-if="state === TaskState.SUCCESS && isUpload"
                src="@assets/h5/svgs/preload-icon-transmission-item-open.svg"
                alt=""
                @click="onOpenInParentDir"
              />
              <img
                v-else-if="state === TaskState.FAILED && task.transmissionTaskType !== TransmissionTaskType.ZIP"
                src="@assets/h5/svgs/preload-icon-transmission-item-refresh.svg"
                alt=""
                @click="onResume"
              />
              <img
                v-else-if="!(!isUpload && state === TaskState.SUCCESS)"
                src="@assets/h5/svgs/preload-icon-transmission-item-cancel.svg"
                alt=""
                @click="onDelete"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
<script setup lang="ts">
  import type { PropType } from 'vue'
  import { ref, watch, computed } from 'vue'
  import { getIconBySuffix } from '../fileutil/FileIconHelper'
  import type { TaskInfo } from '@/core/transmission/ITask'
  import { TaskState, TransmissionTaskType } from '@/core/transmission/ITask'
  import { INSTANCE as FileUploadManager } from '@/core/transmission/FileUploadManager'
  import { INSTANCE as FileDownloadManager } from '@/core/transmission/FileDownloadManager'
  import { INSTANCE as ApplicationManager } from '@core/application/ApplicationManager'
  import { encodeRelativePathWithParams, formatBytes, saveLinkAsFile } from '@/core/Utils'
  import { TOOLTIP_SHOW_IN_SECONDS } from '@components/Constants'
  import type { DownloadTaskDirectly } from '@/core/transmission/DownloadTaskDirectly'
  import EllipsisComponent from './EllipsisComponent.vue'
  import { NoChannelAvailableException } from '@/core/Exceptions'
  import { useSharedActiveMainHeaderItem, MainHeaderItems } from '@/core/compositions/ActiveMainHeaderItem'
  import { useI18n } from 'vue-i18n'
  import { useSharedScreen } from '@/core/compositions/Screen'
  import { INSTANCE as DeviceManager } from '@core/devices/DeviceManager'
  import { ConnectionState } from '@core/devices/IDevice'
  import { INSTANCE as DevicePullUpTaskManager } from '@core/DevicePull/DevicePullUpTaskManager'
  import { debounce } from 'lodash-es'
  import { TimeConstants } from '@/core/Constants'

  const { isStandardScreen } = useSharedScreen()
  const { t } = useI18n()
  const { updateCurrentSelectedItem } = useSharedActiveMainHeaderItem()

  const labelHighSpeed = '高速'

  const labelSpeed = computed(() => (props.task.isViaLan ? labelHighSpeed : ''))

  const UploadTaskLabelMap = new Map<TaskState, string>([
    [TaskState.IDLE, 'owork_base.transmission.task_state_upload_idle'],
    [TaskState.GOING, 'owork_base.transmission.task_state_upload_going'],
    [TaskState.WAITING, 'owork_base.transmission.task_state_upload_waiting'],
    [TaskState.PAUSE, 'owork_base.transmission.task_state_upload_pause'],
    [TaskState.FAILED, 'owork_base.transmission.task_state_upload_failed'],
    [TaskState.SUCCESS, 'owork_base.transmission.task_state_upload_success'],
  ])

  const DownloadTaskLabelMap = new Map<TaskState, string>([
    [TaskState.IDLE, 'owork_base.transmission.task_state_download_idle'],
    [TaskState.GOING, 'owork_base.transmission.task_state_download_going'],
    [TaskState.SUCCESS, 'owork_base.transmission.task_state_download_success'],
    [TaskState.FAILED, 'owork_base.transmission.task_state_download_failed'],
    [TaskState.PAUSE, 'owork_base.transmission.task_state_download_pause'],
    [TaskState.WAITING, 'owork_base.transmission.task_state_download_waiting'],
  ])

  const CompressTaskLabelMap = new Map<TaskState, string>([
    [TaskState.IDLE, 'owork_base.transmission.task_state_compress_idle'],
    [TaskState.GOING, 'owork_base.transmission.task_state_compress_going'],
    [TaskState.SUCCESS, 'owork_base.transmission.task_state_compress_success'],
    [TaskState.FAILED, 'owork_base.transmission.task_state_compress_failed'],
    [TaskState.PAUSE, 'owork_base.transmission.task_state_compress_pause'],
    [TaskState.WAITING, 'owork_base.transmission.task_state_compress_waiting'],
  ])
  const TaskErrorStrokeColor = 'rgba(235, 59, 47, 1)'
  const TaskDefaultStrokeColor = 'rgba(0, 102, 255, 1)'
  const TaskSuccessStrokeColor = 'rgba(0, 153, 15, 1)'

  const TaskErrorLabelColor = 'rgba(219, 56, 44, 1)'
  const TaskDefaultLabelColor = ''
  const TaskSuccessLabelColor = ''
  const props = defineProps({
    task: {
      type: Object as PropType<TaskInfo>,
      default: null,
      require: true,
    },
    isUpload: Boolean,
    isDesktopEnv: Boolean,
  })

  const transmissionManager = props.isUpload ? FileUploadManager : FileDownloadManager
  const state = ref(-1)

  const INIT_PLACEHOLDER = '--'

  const fileSize = computed(() => {
    if (props.task.fileSize === -1) return
    const fileSize = formatBytes(props.task.fileSize)

    return {
      withoutUnit: Number(fileSize.split(' ').shift()!),
      withUnit: fileSize,
    }
  })

  const progress = ref(0)

  const progressPresent = computed(() => {
    if (fileSize.value) {
      return (
        ((fileSize.value.withoutUnit * props.task.completionRatio) / 100).toFixed(2) + ` / ` + fileSize.value.withUnit
      )
    }

    return `${INIT_PLACEHOLDER}/${INIT_PLACEHOLDER}`
  })
  // TODO: 后续通过class类名实现颜色切换
  const progressColumnColor = computed(() => {
    const progressColumnColor = {
      strokeColor: TaskDefaultStrokeColor,
      labelColor: TaskDefaultLabelColor,
    }

    switch (state.value) {
      case TaskState.FAILED:
        progressColumnColor.strokeColor = TaskErrorStrokeColor
        progressColumnColor.labelColor = TaskErrorLabelColor
        break
      case TaskState.SUCCESS:
        progressColumnColor.strokeColor = TaskSuccessStrokeColor
        progressColumnColor.labelColor = TaskSuccessLabelColor
        break
    }

    return progressColumnColor
  })

  const labelState = computed(() => {
    switch (props.task.transmissionTaskType) {
      case TransmissionTaskType.UPLOAD:
        return t(UploadTaskLabelMap.get(state.value)!)
      case TransmissionTaskType.DOWNLOAD:
        return t(DownloadTaskLabelMap.get(state.value)!)
      default:
        return t(CompressTaskLabelMap.get(state.value)!)
    }
  })

  const zipProgress = computed(() => {
    if (progress.value <= 0) {
      return t('owork_base.transmission.task_state_compress_file_being_prepared')
    } else {
      return `${labelState.value} ${progress.value <= 0.01 ? 0.01 : progress.value.toFixed(2)}%`
    }
  })

  const toolTipMsg = computed(() => {
    if (props.task.deviceName && props.task.path) {
      return `${props.task.deviceName}${props.task.path}${props.task.fileName}`
    }

    return ''
  })
  const debouncePullUpDevice = debounce(
    () => {
      DevicePullUpTaskManager.pullUpDevice({ deviceId: props.task.sourceDeviceId }, undefined, true, true)
    },
    TimeConstants.DEFAULT_DEBOUNCING_IN_MS,
    {
      leading: true,
      trailing: false,
    }
  )

  const onRestart = () => {
    if (DeviceManager.getDeviceById(props.task.sourceDeviceId)?.connectionState !== ConnectionState.CONNECTED) {
      debouncePullUpDevice()
    } else {
      transmissionManager.restartTaskById(props.task.id)
    }
  }

  const onDelete = () => {
    transmissionManager.removeTaskById(props.task.id)
  }

  const onPause = () => {
    transmissionManager.pauseTaskById(props.task.id)
  }

  const onOpenInParentDir = () => {
    //open file
    if (props.task.relativePath) {
      const { relativePath, deviceId = '', fileName, targetPath = '', appId } = props.task
      const pageManager = ApplicationManager.getPageManager(appId)

      if (!pageManager) return
      const path = encodeRelativePathWithParams(relativePath, {
        deviceId,
        targetPath,
        fileName,
      })

      updateCurrentSelectedItem(MainHeaderItems.TRANSMISSION_LIST)
      pageManager.goto(path)
    }
  }

  const onResume = () => {
    if (DeviceManager.getDeviceById(props.task.sourceDeviceId)?.connectionState !== ConnectionState.CONNECTED) {
      debouncePullUpDevice()
    } else {
      transmissionManager.resumeTaskById(props.task.id)
    }
  }

  const onDownload = async () => {
    //TODO: 增加downloadUrl 不存在时的提示
    const task = FileDownloadManager.getTaskById(props.task.id)

    if (!task) return

    if (props.task.isViaLan) {
      try {
        const downloadUrl = await (task as DownloadTaskDirectly)?.getDownloadUrl()

        downloadUrl && saveLinkAsFile(downloadUrl, props.task.fileName)
      } catch (error) {
        if (error instanceof NoChannelAvailableException) {
          //TODO: 临时解决链接超时跳转白屏的问题，后续通过处理方案优化
        }
      }
    } else {
      props.task.downloadUrl && saveLinkAsFile(props.task.downloadUrl, props.task.fileName)
    }
  }

  watch(
    () => props.task,
    () => {
      progress.value = props.task.completionRatio > 100 ? 100 : props.task.completionRatio
      state.value = props.task.state
    },
    { deep: true, immediate: true }
  )
</script>
<style scoped lang="stylus">
  @import '../styles/transfer.styl'

  .progress-container
    flex-align-center()
    flex-direction row
    flex 1
    .progress-bar
      width 88px
      :deep(.ant-progress-inner)
        background-color rgba(0, 0, 0, 0.12)
    .progress-text
    .label-status
      width 100%
      color default_shallow_color
      padding-left 8px
      flex 1
      white-space nowrap
      display flex
      align-items center

  .operation-container
    display flex
    justify-content space-between
    .operation-btn
      & > img
        cursor pointer
        rect(16px)

  @import '../styles/h5Adaptive/Transmission.styl'
</style>
